import React from "react"
import { pipe, flow } from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as React_ from "elm-ts/lib/React"
import * as A from "fp-ts/Array"
import * as O from "fp-ts/Option"
import * as M from "../Model/feedbackModel"
import { BrandSpinner, Icon, Modal } from "../../components"
import { CalendlyModal, Error } from "../components"
import { AgentFeedback, threadTagValue, Variant } from "@flypengine/myFlyp-lib"
import useCarrousel from "../../hooks/useCarrousel"
import dayjs from "dayjs"

export function feedbackView<msg>(
  config: M.Config<msg>
): (model: M.Model) => React_.Html<msg> {
  return model => dispatch => {
    return pipe(
      model,
      E.match(
        status => {
          switch (status) {
            case "Not Attempted": {
              return <></>
            }
            case "Loading": {
              return (
                <div data-cy="dashboard-feedback-loader">
                  <BrandSpinner description="Loading agent feedback" />
                </div>
              )
            }
            case "Error": {
              return (
                <Error
                  dataCy="dashboard-feedback-error"
                  message=" Data not found. If this doesn't look right please"
                />
              )
            }
            case "Not Authorized": {
              return (
                <Error
                  dataCy="dashboard-feedback-error-auth"
                  message="You are not authorized to see this content. If this doesn't look right please"
                />
              )
            }
          }
        },
        ({ agentDealsModel, CSCalendarUrl, modal }) => (
          <>
            {viewAgentThreadList({
              contents: { agentDealsModel, CSCalendarUrl },
              updateModalContents: modal =>
                dispatch(config.wrapperMsg(M.Msg.modalContents(modal))),
            })(flow(config.wrapperMsg, dispatch))}
            {viewModal(modal)(flow(config.wrapperMsg, dispatch))}
          </>
        )
      )
    )
  }
}

const viewAgentThreadList = ({
  contents,
  updateModalContents,
}: {
  contents: Pick<M.PageModel, "agentDealsModel" | "CSCalendarUrl">
  updateModalContents: (modal: O.Option<M.Modal>) => void
}): React_.Html<M.Msg> => {
  return dispatch =>
    contents.agentDealsModel.agentDeals.length === 0 ? (
      <div className=" md:px-6 lg:px-10 py-5 max-w-[1400px] mx-auto ">
        <div className="flex flex-col min-h-[80vh] px-10">
          <div className="m-auto w-4/5 sm:w-2/5">
            <div className="flex flex-col min-h-[80vh] w-70vw justify-center">
              <h1 className="mb-8">No results found</h1>
              <p className="text-lg" data-cy={"dashboard-feedback-empty"}>
                If this doesn't look right please{" "}
                <a
                  className="underline text-brick"
                  href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                >
                  get in touch
                </a>
                <br />
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div className="md:px-6 lg:px-10 py-5 max-w-[1400px] mx-auto ">
        <div
          className={`card p-0 ${
            O.isSome(contents.CSCalendarUrl)
              ? "max-h-[calc(100svh-250px)]"
              : "max-h-[calc(100svh-120px)]"
          } 
           md:overflow-auto md:shadow-xl`}
        >
          <div className=" md:sticky top-0 px-6 lg:px-10 bg-white z-30 ">
            <div className="py-4 lg:py-6 flex items-center justify-between flex-wrap md:flex-row  flex-col gap-x-6 gap-y-2 border-b border-grey ">
              <h2
                className="grow  text-3xl sm:text-[2rem] text-center sm:text-left"
                data-cy="feedback-counter"
              >
                Agent feedback (
                {M.totalThreadsPerDeal(contents.agentDealsModel.agentDeals)})
              </h2>
              <div className="flex justify-center flex-wrap gap-2  ">
                {contents.agentDealsModel.tags.map(({ tag, count }) => (
                  <div
                    className=" tag text-sm bg-concrete-light cursor-default"
                    data-cy={`feedback-tag-${tag}`}
                    key={`${tag}-${count}`}
                  >
                    {tag} ({count})
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className=" px-6 md:pr-0 lg:pl-10 pb-12">
            {contents.agentDealsModel.agentDeals.map(agentDeal => (
              <div
                data-cy={`feedback-agent-${agentDeal.id}`}
                key={`row-${agentDeal.id}`}
                className=" group grid grid-cols-1 md:grid-cols-[220px,1fr] relative "
              >
                <div className="  border-grey md:border-r md:border-b group-last:border-b-0 pt-8 md:pb-6 pr-4 text-sm flex flex-col items-center md:items-start ">
                  <h4 className=" mb-1 leading-snug line-clamp-2 text-center md:text-left [text-wrap:balance]">
                    {agentDeal.brand}
                  </h4>
                  <p className=" mb-3 text-xs">
                    {O.getOrElse(() => "")(agentDeal.branch)}
                  </p>
                  <AgentDealStatus
                    status={agentDeal.status}
                    summary={agentDeal.summary}
                    website={agentDeal.website}
                  />
                </div>
                <ThreadList
                  threads={agentDeal.threads}
                  onSelect={(thread, id) => {
                    dispatch(M.Msg.lastFocused(O.some(id)))
                    dispatch(
                      M.Msg.modalContents(
                        O.some(
                          M.Modal.ThreadModal({
                            brand: agentDeal.brand,
                            branch: agentDeal.branch,
                            ...thread,
                          })
                        )
                      )
                    )
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        {pipe(
          contents.CSCalendarUrl,
          O.match(
            () => <></>,
            calendar => (
              <div className="px-2 py-4 bg-opacity-90 bg-white md:bg-transparent w-full flex fixed md:relative bottom-0 m-0 md:my-4">
                <button
                  className="button primary md:w-fit w-full text-md text-center block mx-auto"
                  data-cy="dashboard-feedback-cs-calendar-btn"
                  onClick={() =>
                    updateModalContents(
                      O.some(M.Modal.CalendlyModal({ calendarId: calendar }))
                    )
                  }
                >
                  Contact us
                </button>
              </div>
            )
          )
        )}
      </div>
    )
}
const AgentDealStatus = (
  data: Pick<AgentFeedback, "status" | "summary" | "website">
): JSX.Element =>
  pipe(
    data.status,
    O.match(
      () => <></>,
      ({ status }) =>
        pipe(
          status,
          Variant.caseOf({
            accepted: () => (
              <>
                <div className="flex gap-2 flex-wrap justify-center md:justify-start">
                  <p className="text-xs mb-1 pr-4 flex items-center gap-2 capitalize md:w-full">
                    <span className=" w-4">
                      <Icon.CheckboxChecked />
                    </span>
                    Accepted
                  </p>
                  {data.summary.viewings > 0 && (
                    <p className="text-xs px-2 py-1 bg-stone-light rounded">
                      <span className=" font-mono">
                        {data.summary.viewings}
                      </span>{" "}
                      {data.summary.viewings === 1 ? "viewing" : "viewings"}
                    </p>
                  )}
                  {data.summary.offers > 0 && (
                    <p className="text-xs px-2 py-1 bg-stone-light rounded">
                      <span className=" font-mono">{data.summary.offers}</span>{" "}
                      {data.summary.offers === 1 ? "offer" : "offers"}
                    </p>
                  )}
                  {pipe(
                    data.website,
                    O.match(
                      () => <></>,
                      websiteUrl => (
                        <p className="text-xs px-2 py-1 bg-stone-light rounded">
                          <a
                            className="text-xs"
                            href={websiteUrl}
                            target="_blank"
                          >
                            On agent website{" "}
                            <span className="inline-block w-2 h-2">
                              <Icon.ExternalLink />
                            </span>
                          </a>
                        </p>
                      )
                    )
                  )}
                </div>
              </>
            ),
            pending: () => (
              <p className=" mb-3 flex items-center gap-2 text-sm capitalize">
                <span className="w-3 h-3 rounded bg-gray-300"></span>{" "}
                <span>Pending</span>
              </p>
            ),
            rejected: reason => (
              <p className=" mb-3 flex items-baseline gap-2 text-sm capitaliz">
                <span className="w-3 h-3 rounded bg-brick grid place-items-center">
                  <Icon.Cross />
                </span>
                <span className="w-full">Rejected ({reason})</span>
              </p>
            ),
          })
        )
    )
  )

const ThreadList = ({
  threads,
  onSelect,
}: {
  threads: M.ThreadWithMetadata[]
  onSelect: (thread: M.ThreadWithMetadata, id: string) => void
}): JSX.Element => {
  const container = React.useRef<HTMLDivElement>(null)
  const prevButton = React.useRef<HTMLButtonElement>(null)
  const nextButton = React.useRef<HTMLButtonElement>(null)

  const { scrollContainerBy } = useCarrousel({
    containerRef: container,
    prevButtonRef: prevButton,
    nextButtonRef: nextButton,
  })

  return (
    <>
      <div className="absolute z-10 right-10 bottom-8 flex">
        {threads.length > 1 && (
          <>
            <button
              ref={prevButton}
              className="button-icon hidden md:block rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("left")}
              data-cy="feedback-topics-left"
              aria-label="scroll carousel left"
            >
              <Icon.ChevronRight />
            </button>
            <button
              ref={nextButton}
              className=" ml-1 button-icon hidden md:block disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000] "
              onClick={() => scrollContainerBy("right")}
              data-cy="feedback-topics-right"
              aria-label="scroll carousel right"
            >
              <Icon.ChevronRight />
            </button>
          </>
        )}
      </div>
      <div
        className={
          "carousel lg:gap-5 items-start py-6 h-[248px] px-6 lg:px-10 w-screen md:w-full border-grey border-b group-last:border-b-0 [--set-d:24px] md:[--set-d:0px]" +
          (threads?.length === 1
            ? " justify-center md:justify-start"
            : " justify-start")
        }
        ref={container}
      >
        <>
          {threads.length < 1 ? (
            <div className=" flex-[0_0_auto] w-full h-[195px] grid place-items-center text-center opacity-50 ">
              <div className=" col-start-1 row-start-1 w-[160px] px-4 -z-10 [--icon-fill:var(--concrete)] ">
                <Icon.House />
              </div>
              <h4 className=" col-start-1 row-start-1 mt-2 leading-snug [text-wrap:balance]">
                Nothing yet - Great things are coming your way!
              </h4>
            </div>
          ) : (
            <>
              {threads.map(thread => {
                return (
                  <button
                    id={`thread-${thread.id}-btn`}
                    key={thread.id}
                    onClick={() => onSelect(thread, `thread-${thread.id}-btn`)}
                    data-cy={`feedback-thread-${thread.id}`}
                    className={`
                       flex-[0_0_auto] w-[min(calc(100%-24px),380px)] sm:w-[max(420px,50%)] max-h-[199px] card  flex flex-col text-left ${
                         thread.categories.includes("Flyp")
                           ? " bg-concrete-light"
                           : "bg-concrete"
                       }`}
                  >
                    <div className=" mb-2 w-full flex items-center gap-2">
                      <span className=" font-mono">
                        {thread.comments.length}
                      </span>
                      <span className=" inline-block w-3 h-3">
                        <Icon.ChatBuble />
                      </span>
                      <span className="grow pl-2">
                        {pipe(
                          thread.lastComment,
                          O.match(
                            () => <></>,
                            ({ createdAt }) => (
                              <>{dayjs(createdAt).format("Do MMM")}</>
                            )
                          )
                        )}
                      </span>
                      <div className=" flex gap-2 text-sm ">
                        {pipe(
                          thread.tags,
                          A.map(
                            flow(
                              threadTagValue,
                              O.match(
                                () => <></>,
                                ({ tag }) =>
                                  pipe(
                                    tag,
                                    O.match(
                                      () => <></>,
                                      tagLabel => (
                                        <span
                                          className="bg-white bg-opacity-70 px-2 py-1 rounded hidden first:block lg:block "
                                          key={`${thread.id}-${tagLabel}`}
                                        >
                                          {tagLabel}
                                        </span>
                                      )
                                    )
                                  )
                              )
                            )
                          ),
                          A.takeLeft(3)
                        )}
                      </div>
                    </div>
                    <h5 className=" mb-2 text-ellipsis overflow-hidden whitespace-pre w-full leading-snug capitalize">
                      {thread.subject}
                    </h5>
                    {pipe(
                      thread.lastComment,
                      O.match(
                        () => <></>,
                        ({ html }) => (
                          <>
                            <div
                              className="w-full rt-preview line-clamp-4 text-sm "
                              dangerouslySetInnerHTML={{ __html: html }}
                            ></div>
                          </>
                        )
                      )
                    )}
                  </button>
                )
              })}
            </>
          )}
        </>
      </div>
    </>
  )
}

function viewModal(modal: O.Option<M.Modal>) {
  return Modal({
    isOpen: O.isSome(modal),
    closeMsg: M.Msg.modalContents(O.none),
    containerStyles: "",
    body: _dispatch =>
      pipe(
        modal,
        O.match(
          () => <></>,
          modal =>
            pipe(
              modal,
              Variant.caseOf({
                [M._CalendlyModal]: contents => (
                  <div
                    className="card"
                    data-cy="dashboard-feedback-cs-calendar-modal"
                  >
                    <CalendlyModal calendarId={contents.calendarId} />
                  </div>
                ),
                [M._ThreadModal]: thread => (
                  <div className="card bg-concrete" data-cy="feedback-modal">
                    <div className=" mb-4 flex justify-between">
                      <p className="flex items-center gap-2">
                        <span className=" inline-block w-3 h-3">
                          <Icon.ChatBuble />
                        </span>
                        <span className=" font-mono ">
                          {thread.comments.length}
                        </span>
                        <span>
                          {thread.comments.length === 1
                            ? "Comment"
                            : "Comments"}
                        </span>
                      </p>
                    </div>
                    <div>
                      <h1 className="mb-4 line-clamp-2 leading-snug ">
                        {thread.subject}
                      </h1>
                      <div className=" mb-6 flex gap-4 items-start">
                        {pipe(
                          thread.tags,
                          A.map(
                            flow(
                              threadTagValue,
                              O.match(
                                () => <></>,
                                ({ category, tag }) => (
                                  <>
                                    {pipe(
                                      tag,
                                      O.match(
                                        () => <></>,
                                        label => (
                                          <span
                                            className="tag text-base bg-white bg-opacity-30"
                                            key={`${thread.id}-${category}-${label}`}
                                          >
                                            {category} - {label}
                                          </span>
                                        )
                                      )
                                    )}
                                  </>
                                )
                              )
                            )
                          )
                        )}
                      </div>
                      <dl className=" mb-10 ">
                        <div className=" mb-4 flex gap-4 items-start">
                          <dt className="flex-[0_0_auto] w-20 md:w-32 flex flex-row gap-2 items-center font-bold ">
                            <div className="w-[16px]">
                              <Icon.Agent />
                            </div>
                            <span>Agent</span>
                          </dt>
                          <dd>{thread.brand}</dd>
                        </div>
                        {pipe(
                          thread.lastComment,
                          O.match(
                            () => <></>,
                            ({ createdAt }) => (
                              <div className=" mb-4 flex gap-4 items-start">
                                <dt className=" flex-[0_0_auto] w-20 md:w-32 flex flex-row gap-2 items-center font-bold ">
                                  <div className="w-[16px]">
                                    <Icon.Clock />
                                  </div>
                                  <span>Date</span>
                                </dt>
                                <dd>
                                  {dayjs(createdAt).format("Do MMM YYYY")}
                                </dd>
                              </div>
                            )
                          )
                        )}
                      </dl>
                      <div className="mb-4 border-black border-b border-opacity-10"></div>
                    </div>
                    <h6 className="font-sans ">Comments</h6>
                    <div className="mb-6">
                      {pipe(
                        M.commentsByDate(thread.comments),
                        A.map(([day, comments]) => (
                          <>
                            <div
                              className=" text-base text-slate text-opacity-60 pt-6 pb-4"
                              key={`${thread.id}-${day}`}
                            >
                              {dayjs(day).format("DD MMM YYYY")}
                            </div>
                            {comments.map(({ createdAt, html }) => (
                              <div className="flex flex-col md:flex-row gap-4 mb-4 last:mb-6 w-full ">
                                <p className="flex-0  text-slate text-sm opacity-60">
                                  <span className=" inline-block w-3 h-3 mr-3">
                                    <Icon.ChatBuble />
                                  </span>
                                  {dayjs(createdAt).format("HH:mm")} h
                                </p>
                                <div
                                  className="flex-1 rt-wrap-links"
                                  dangerouslySetInnerHTML={{ __html: html }}
                                ></div>
                              </div>
                            ))}
                          </>
                        ))
                      )}
                    </div>
                    <div className=" pt-6 flex justify-center border-t border-gray-300">
                      <a
                        href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                        className="button primary"
                      >
                        Contact us
                      </a>
                    </div>
                  </div>
                ),
              })
            )
        )
      ),
  })
}
